import { VirtualWarehouseItem, getItemKey } from "@api/allocateManagementApi";
import ImportFileModal from "@component/ImportFileModal";
import PageTitle from "@component/PageTitle";
import { AllocationChannelOption, allocationChannelOptions } from "@constant/AllocationChannel";
import { RetailVsCodeFilter } from "@constant/ThroughShipment";
import BatchAllocationModal from "@page/AllocateManagement/BatchAllocationModal";
import SingleAllocationModal from "@page/AllocateManagement/SingleAllocationModal";
import { fetchRetailList } from "@redux/dealerSlice";
import {
  clearImportSuccess,
  fetchVirtualWarehouseList,
  importAllocateOrder,
  resetVirtualWarehouse,
  setVirtualWarehouseFilter,
  updateImportErrorResults,
} from "@redux/sharing/virtualWarehouseSlice";
import sortRetailByPinned from "@utils/retailerUtils";

import React, { useEffect, useMemo, useState } from "react";
import { FilterOutlined, UploadOutlined } from "@ant-design/icons";
import { Button, DatePicker, Form, Input, Modal, Select, Table } from "antd";
import locale from "antd/es/date-picker/locale/zh_TW";
import { ColumnsType } from "antd/lib/table";
import type { Moment } from "moment";
import styled from "styled-components";

import { useAppDispatch, useSelector } from "src/store";

const { Option } = Select;

const CHANNEL_ONLY_KEYS = new Set(["retailerName", "allocatedQty"]);

const Wrapper = styled.div`
  padding: 20px 20px 20px 25px;
`;

const FilterForm = styled(Form)`
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: auto auto auto;
  gap: 12px 24px;
  margin-bottom: 16px;
  align-items: center;

  .ant-form-item {
    margin-bottom: 0;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 13px;
  margin-top: 4px;
  justify-content: flex-end;
`;

const TableWrapper = styled.div`
  border: solid 1px #f0f0f0;
  border-radius: 2px;
  padding: 20px 10px 22px 17px;
  margin-top: 20px;
`;

const ActionRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
`;

const PaginationRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
`;

const TableHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-block: 5px;
`;

const PageText = styled.span`
  color: rgba(0, 0, 0, 0.65);
`;

const SelectPageSize = styled(Select)`
  width: 80px;
`;

const ModalContent = styled.section`
  max-height: 150px;
  overflow: auto;
`;

interface FilterFormValues {
  sku: string[];
  channelId: number;
  retailerId?: number;
  productName: string;
  effectiveDateRange?: [Moment | null, Moment | null] | null;
  batch: string;
}

export default function VirtualWarehousePage() {
  const dispatch = useAppDispatch();
  const virtualWarehouseList = useSelector((state) => state.virtualWarehouseSlice.list);
  const filter = useSelector((state) => state.virtualWarehouseSlice.filter);
  const isFetching = useSelector((state) => state.virtualWarehouseSlice.isFetching);
  const isFetchingImport = useSelector((state) => state.virtualWarehouseSlice.isFetchingImport);
  const importSuccess = useSelector((state) => state.virtualWarehouseSlice.importSuccess);
  const importErrorResults = useSelector((state) => state.virtualWarehouseSlice.importErrorResults);
  const { retailList } = useSelector((state) => state.dealerSlice);
  const [page, setPage] = useState<number>(1);
  const [pageLimit, setPageLimit] = useState<number>(20);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [showImportModal, setShowImportModal] = useState<boolean>(false);
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  const [batchModalVisible, setBatchModalVisible] = useState<boolean>(false);
  const [singleAllocationItem, setSingleAllocationItem] = useState<VirtualWarehouseItem | undefined>(undefined);

  const [filterForm] = Form.useForm<FilterFormValues>();
  const channelId = Form.useWatch("channelId", filterForm) ?? allocationChannelOptions[0].channelId;
  const sourceDealerInput: AllocationChannelOption =
    allocationChannelOptions.find((o) => o.channelId === channelId) ?? allocationChannelOptions[0];

  useEffect(() => {
    dispatch(fetchVirtualWarehouseList(filter));
  }, [dispatch, filter]);

  useEffect(() => {
    if (sourceDealerInput.kind === "retailer") {
      dispatch(
        fetchRetailList({ regionTypes: sourceDealerInput.channelId, isVs: RetailVsCodeFilter.HAS_VS_CODE }),
      );
    }
  }, [dispatch, sourceDealerInput]);

  useEffect(() => {
    if (importSuccess) {
      setShowImportModal(false);
      dispatch(clearImportSuccess());
    }
  }, [dispatch, importSuccess]);

  useEffect(() => {
    if (importErrorResults) {
      Modal.warning({
        title: "部分商品資料有誤，請修正",
        content: (
          <ModalContent>
            {importErrorResults.map((errorItem) => (
              <p key={errorItem}>{errorItem}</p>
            ))}
          </ModalContent>
        ),
        okText: "我知道了",
        onOk: () => dispatch(updateImportErrorResults(undefined)),
      });
    }
  }, [dispatch, importErrorResults]);

  useEffect(() => {
    return () => {
      dispatch(resetVirtualWarehouse());
    };
  }, [dispatch]);

  const handleOnSearch = () => {
    const values = filterForm.getFieldsValue();
    setPage(1);
    const channel = allocationChannelOptions.find((o) => o.channelId === values.channelId) ?? allocationChannelOptions[0];
    dispatch(
      setVirtualWarehouseFilter({
        ...filter,
        sku: values.sku.length > 0 ? values.sku : undefined,
        regionType: channel.kind === "warehouse" ? undefined : channel.channelId,
        retailerId: values.retailerId,
        productName: values.productName || undefined,
        effectiveDateAfter: values.effectiveDateRange?.[0] ? values.effectiveDateRange[0].format("YYYY-MM-DD") : undefined,
        effectiveDateBefore: values.effectiveDateRange?.[1] ? values.effectiveDateRange[1].format("YYYY-MM-DD") : undefined,
        batch: values.batch || undefined,
        offset: 0,
      }),
    );
  };

  const handleOnValuesChange = (changedValues: Partial<FilterFormValues>) => {
    if ("channelId" in changedValues) {
      filterForm.setFieldValue("retailerId", undefined);
    }
  };

  const handleOnReset = () => {
    filterForm.resetFields();
    setPage(1);
    dispatch(
      setVirtualWarehouseFilter({
        sku: undefined,
        regionType: undefined,
        retailerId: undefined,
        productName: undefined,
        effectiveDateAfter: undefined,
        effectiveDateBefore: undefined,
        batch: undefined,
        limit: pageLimit,
        offset: 0,
      }),
    );
  };

  const handleOnPageChange = (value: number) => {
    window.scrollTo(0, 0);
    setPage(value);
    dispatch(setVirtualWarehouseFilter({ ...filter, offset: (value - 1) * filter.limit }));
  };

  const handleOnPageLimitChange = (value: unknown) => {
    const limit = parseInt(String(value), 10);
    setPageLimit(limit);
    setPage(1);
    dispatch(setVirtualWarehouseFilter({ ...filter, limit, offset: 0 }));
  };

  const handleOnAllocationSuccess = () => {
    setBatchModalVisible(false);
    setSingleAllocationItem(undefined);
    setSelectedRowKeys([]);
    dispatch(fetchVirtualWarehouseList(filter));
  };

  const retailOptions = useMemo(() => {
    const options = retailList.results.map((r) => ({ label: r.name, value: r.id }));
    return sortRetailByPinned(options);
  }, [retailList.results]);

  const selectedItems = useMemo(
    () => virtualWarehouseList.results.filter((r) => selectedRowKeys.includes(getItemKey(r))),
    [virtualWarehouseList.results, selectedRowKeys],
  );

  const renderExpiryBatch = (record: VirtualWarehouseItem) => {
    const { effectiveDate, batch } = record;
    if (!effectiveDate) return "-";
    return batch ? `${effectiveDate}_${batch}` : effectiveDate;
  };

  const isChannelMode = filter.regionType !== undefined;

  const allColumns: ColumnsType<VirtualWarehouseItem> = [
    { key: "sku", dataIndex: "sku", title: "SKU" },
    { key: "retailerName", dataIndex: "retailerName", title: "通路", render: (v: string | null) => v ?? "-" },
    { key: "productName", dataIndex: "productName", title: "商品名稱" },
    { key: "expiryBatch", title: "效期_批號", render: (_, record) => renderExpiryBatch(record) },
    { key: "allocatedQty", dataIndex: "allocatedQty", title: "通路庫存數" },
    { key: "canUseQty", dataIndex: "canUseQty", title: "當前可分配數" },
    { key: "totalStock", dataIndex: "totalStock", title: "實際倉庫數量" },
    {
      key: "action",
      title: "",
      render: (_, record) => (
        <Button type="link" onClick={() => setSingleAllocationItem(record)}>
          配貨
        </Button>
      ),
    },
  ];

  const columns = isChannelMode ? allColumns : allColumns.filter((col) => !CHANNEL_ONLY_KEYS.has(String(col.key)));

  return (
    <Wrapper>
      <PageTitle title="虛擬倉" />
      <ActionRow>
        <Button type="primary" icon={<UploadOutlined />} onClick={() => setShowImportModal(true)}>
          匯入配貨單
        </Button>
        <Button icon={<FilterOutlined />} onClick={() => setIsFilterOpen((prev) => !prev)}>
          篩選
        </Button>
      </ActionRow>
      <FilterForm
        form={filterForm}
        colon={false}
        style={{ display: isFilterOpen ? "block" : "none" }}
        initialValues={{
          sku: [],
          channelId: allocationChannelOptions[0].channelId,
          productName: "",
          batch: "",
        }}
        onValuesChange={handleOnValuesChange}
        onFinish={handleOnSearch}
      >
        <FilterGrid>
          <Form.Item label="SKU" name="sku" style={{ marginBottom: 0 }}>
            <Select mode="tags" placeholder="輸入 SKU" style={{ minWidth: 200 }} tokenSeparators={[","]} />
          </Form.Item>
          <Form.Item label="通路" style={{ marginBottom: 0 }}>
            <div style={{ display: "flex", gap: 8 }}>
              <Form.Item name="channelId" noStyle>
                <Select
                  style={{ flex: 1 }}
                  options={allocationChannelOptions.map((o) => ({ label: o.label, value: o.channelId }))}
                />
              </Form.Item>
              {sourceDealerInput.kind === "retailer" && (
                <Form.Item name="retailerId" noStyle>
                  <Select
                    style={{ flex: 1 }}
                    showSearch
                    allowClear
                    placeholder="請選擇或直接輸入"
                    options={retailOptions}
                    filterOption={(input, option) =>
                      typeof option?.label === "string" && option.label.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              )}
            </div>
          </Form.Item>
          <Form.Item label="商品名稱" name="productName" style={{ marginBottom: 0 }}>
            <Input />
          </Form.Item>
          <Form.Item label="效期" name="effectiveDateRange" style={{ marginBottom: 0 }}>
            <DatePicker.RangePicker locale={locale} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="批號" name="batch" style={{ marginBottom: 0 }}>
            <Input />
          </Form.Item>
          <ButtonRow>
            <Button onClick={handleOnReset}>清除篩選條件</Button>
            <Button type="primary" htmlType="submit">
              搜尋
            </Button>
          </ButtonRow>
        </FilterGrid>
      </FilterForm>
      <TableHeader>
        <Button type="primary" onClick={() => setBatchModalVisible(true)} disabled={selectedItems.length === 0}>
          批量配貨
        </Button>
        <Button onClick={() => setSelectedRowKeys([])} style={{ marginLeft: "5px" }}>
          清除選擇
        </Button>
        <PaginationRow>
          <PageText>{`總共${virtualWarehouseList.count}筆, 每頁顯示`}</PageText>
          <SelectPageSize defaultValue="20" onChange={handleOnPageLimitChange}>
            <Option value="20">20</Option>
            <Option value="50">50</Option>
            <Option value="100">100</Option>
          </SelectPageSize>
          <PageText>筆</PageText>
        </PaginationRow>
      </TableHeader>
      <TableWrapper>
        <Table
          scroll={{ x: "max-content" }}
          loading={isFetching}
          columns={columns}
          dataSource={virtualWarehouseList.results}
          rowKey={(record) => getItemKey(record)}
          rowSelection={{ selectedRowKeys, onChange: (keys) => setSelectedRowKeys(keys) }}
          pagination={{
            pageSize: filter.limit,
            current: page,
            showSizeChanger: false,
            total: virtualWarehouseList.count,
            onChange: handleOnPageChange,
          }}
        />
      </TableWrapper>
      {showImportModal && (
        <ImportFileModal
          fileType=".xlsx"
          templatePath="admin/template/xls/allocate_stock_template_.xlsx"
          isFetching={isFetchingImport}
          csvErrorMessage=""
          clearErrorMessage={() => {}}
          handleUploadFile={(file) => dispatch(importAllocateOrder(file))}
          closePopup={() => setShowImportModal(false)}
          noticeText={
            <>
              <span style={{ color: "#f16c5d", fontWeight: "bold" }}>
                此功能目前僅支援總倉與 UMART 線上平台相互配貨，請勿於起始位置、配貨位置，填寫其他通路
              </span>
              {"\n註: 匯入上限為100筆"}
            </>
          }
        />
      )}
      {batchModalVisible && (
        <BatchAllocationModal
          selectedItems={selectedItems}
          sourceDealerInput={sourceDealerInput}
          onClose={() => setBatchModalVisible(false)}
          onSuccess={handleOnAllocationSuccess}
        />
      )}
      {singleAllocationItem && (
        <SingleAllocationModal
          sourceItem={singleAllocationItem}
          sourceDealerInput={sourceDealerInput}
          onClose={() => setSingleAllocationItem(undefined)}
          onSuccess={handleOnAllocationSuccess}
        />
      )}
    </Wrapper>
  );
}
